import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AllOrderReturnResponse,
  DeliveryOrder,
} from "../../../@types/DRO/GetAllOrderReturnResponse";
import { DeliveryReturnApi } from "../../../api/DeliveryReturn/DeliveryReturnApi";
import { useLoading } from "../../../views/Loader/LoadingContext";
import DeliveryReturnOrderModal from "../AppModals/DeliveryReturnOrderModal";

interface TableProps {
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  search: string;
  statusFilter?: string;
}

const AllReturnTableComponent: React.FC<TableProps> = ({
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
  search,
  statusFilter,
}) => {
  const [data, setData] = useState<DeliveryOrder[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const { setLoading } = useLoading();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const response: AllOrderReturnResponse =
          await DeliveryReturnApi.api.getReturnedOrderAllList(
            currentPage,
            pageSize,
            search,
            undefined,
            undefined,
            statusFilter || undefined,
            "DELIVERY"
          );

        if (!response.data?.data) {
          throw new Error("Invalid response data");
        }

        setData(response.data.data);
        setTotalPages(response.data.totalPages);
      } catch (error) {
        console.error("Error fetching return orders:", error);
        setData([]);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [currentPage, pageSize, search, statusFilter, setLoading]);

  const getStatusBadge = (
    status: string
  ): { className: string; label: string } => {
    const statusMap: Record<string, { className: string; label: string }> = {
      DONE: {
        className: "bg-green-100 text-green-800 border border-green-300",
        label: "Done",
      },
      IN_PROGRESS: {
        className: "bg-yellow-100 text-yellow-800 border border-yellow-300",
        label: "In Progress",
      },
      PENDING: {
        className: "bg-blue-100 text-blue-800 border border-blue-300",
        label: "Pending",
      },
      CANCELLED: {
        className: "bg-red-100 text-red-800 border border-red-300",
        label: "Cancelled",
      },
    };
    return (
      statusMap[status] || {
        className: "bg-gray-100 text-gray-800 border border-gray-300",
        label: status,
      }
    );
  };

  const handleRowClick = (orderId: number) => {
    setSelectedOrderId(orderId);
    setModalOpen(true);
  };

  const columns = [
    { key: "#", label: "#", width: "w-16" },
    { key: "doId", label: "DO ID", width: "w-24" },
    { key: "reference", label: "Reference", width: "w-32" },
    { key: "vendorName", label: "To Vendor", width: "w-48" },
    // { key: "sourceWarehouseName", label: "Source Warehouse", width: "w-48" },
    // {
    //   key: "destinationWarehouseName",
    //   label: "Destination Warehouse",
    //   width: "w-48",
    // },
    { key: "deliveryWarehouseName", label: "Delivery Warehouse", width: "w-48" },
    { key: "creationDate", label: "Creation Date", width: "w-40" },
    { key: "status", label: "Status", width: "w-32" },
  ];

  return (
    <div className="w-full flex flex-col">
      {/* Table Container */}
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          <div className="overflow-hidden shadow-sm border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      className={`${column.width} px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data?.length > 0 ? (
                  data.map((row, index) => {
                    const statusInfo = getStatusBadge(row.status);
                    const isHighlighted = row.status === "IN_PROGRESS";

                    return (
                      <tr
                        key={row.id}
                        className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                          isHighlighted
                            ? "bg-yellow-50 border-l-4 border-l-yellow-500"
                            : ""
                        }`}
                        onClick={() => handleRowClick(row.id)}
                      >
                        <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {currentPage * pageSize + index + 1}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                          {row.doId}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {row.reference}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {row.vendorName}
                        </td>
                        {/* <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {row.sourceWarehouseName}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {row.destinationWarehouseName}
                        </td> */}
                        <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {row.deliveryWarehouseName}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {row.creationDate}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.className}`}
                          >
                            {statusInfo.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={columns?.length}
                      className="px-4 py-12 text-center text-gray-500"
                    >
                      <div className="flex flex-col items-center">
                        <svg
                          className="w-12 h-12 text-gray-300 mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <p className="text-lg font-medium text-gray-900 mb-1">
                          No return orders found
                        </p>
                        <p className="text-gray-500">
                          Try adjusting your search or filter criteria
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}
            >
              First
            </button>

            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 0))}
              disabled={currentPage === 0}
              className={`p-2 rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              onClick={() =>
                setCurrentPage((p) => Math.min(p + 1, totalPages - 1))
              }
              disabled={currentPage >= totalPages - 1}
              className={`p-2 rounded-md border ${
                currentPage >= totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            <button
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage >= totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}
            >
              Last
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700">
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
        </div>
      )}

      <DeliveryReturnOrderModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        returnOrderId={selectedOrderId}
      />
    </div>
  );
};

export default AllReturnTableComponent;